import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { paginateMeasured, pageContaining } from './measuredPagination';
import { readerTokenText } from '../../components/reader/ReaderTokens';

export function useMeasuredPagination({ tokens, sentenceRanges, sentenceMode, viewportRef, templateRef, fontSize, languageCode, expressionMap, currentPage, setCurrentPage, textId, getSentenceJumpPage }) {
  const [result, setResult] = useState({ pages: [], ready: false });
  const geometryKey = useMemo(() => JSON.stringify(tokens.map(t => [t.token_id, t.surface_form, t.is_word, t.metadata?.is_paragraph_break, t.normalized_form === '¶'])), [tokens]);
  const geometry = useRef({ key: null, tokens: [] });
  if (geometry.current.key !== geometryKey) geometry.current = { key: geometryKey, tokens };
  const geometryTokens = geometry.current.tokens;
  const rangesKey = JSON.stringify(sentenceRanges.map(range => [range.start, range.end]));
  const anchor = useRef({ index: 0, offset: 0 });
  const oldTextId = useRef(textId);
  const current = result.pages[currentPage]?.tokens[0];
  if (current) anchor.current = { index: current._readerIndex, offset: current._readerOffset || 0 };

  useLayoutEffect(() => {
    const viewport = viewportRef.current, template = templateRef.current;
    if (!viewport || !template || !geometryTokens.length) return;
    if (oldTextId.current !== textId) {
      anchor.current = { index: 0, offset: 0 };
      oldTextId.current = textId;
    }
    let cancelled = false;
    let frame;
    const measure = () => {
      if (cancelled) return;
      const width = viewport.clientWidth, height = viewport.clientHeight;
      if (!width || !height) { setResult({ pages: [], ready: true, insufficientSpace: true }); return; }
      const nodes = [...template.children];
      const sandbox = template.cloneNode(false);
      sandbox.removeAttribute('data-testid');
      Object.assign(sandbox.style, { width: `${width}px`, height: 'auto', minHeight: '0', maxHeight: 'none', overflow: 'visible' });
      template.parentNode.appendChild(sandbox);
      const prepared = geometryTokens.map((token, index) => ({ ...token, _readerIndex: index, _readerOffset: 0, _readerText: readerTokenText(token, geometryTokens[index + 1]) }));
      const fits = slice => {
        sandbox.replaceChildren(...slice.map(token => {
          const element = nodes[token._readerIndex].cloneNode(true);
          if (!token.metadata?.is_paragraph_break && token.normalized_form !== '¶') element.textContent = token._readerText;
          return element;
        }));
        // Include glyph/padding boxes, not merely scrollHeight or line count.
        const box = sandbox.getBoundingClientRect();
        if (box.height > height - 4) return false;
        return [...sandbox.children].every(child => {
          // pre-wrap intentionally hangs trailing spaces beyond a justified
          // line. Measure visible glyphs, not invisible whitespace boxes.
          const text = child.firstChild;
          let rects = [...child.getClientRects()];
          if (text?.nodeType === Node.TEXT_NODE) {
            const value = text.textContent;
            const start = value.search(/\S/);
            if (start < 0) return true;
            const range = document.createRange();
            range.setStart(text, start);
            range.setEnd(text, value.trimEnd().length);
            rects = [...range.getClientRects()];
          }
          return rects.every(rect => rect.bottom <= box.top + height - 2 && rect.left >= box.left - 1 && rect.right <= box.right + 1);
        });
      };
      const ranges = JSON.parse(rangesKey).map(([start, end]) => ({ start, end }));
      const measured = paginateMeasured(prepared, sentenceMode ? ranges : [{ start: 0, end: geometryTokens.length }], fits);
      sandbox.remove();
      // If a sentence-jump is pending, let Reader.js decide the target page;
      // otherwise fall back to the anchor-based position.
      let page;
      if (getSentenceJumpPage) {
        const jumpPage = getSentenceJumpPage(measured.pages);
        page = jumpPage !== null ? jumpPage : pageContaining(measured.pages, anchor.current.index, anchor.current.offset);
      } else {
        page = pageContaining(measured.pages, anchor.current.index, anchor.current.offset);
      }
      setResult({ ...measured, ready: true });
      setCurrentPage(page);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    // Layout effect gives the first visible page its final boundaries before paint.
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
    window.addEventListener('resize', schedule);
    return () => {
      cancelled = true; cancelAnimationFrame(frame); observer.disconnect();
      document.fonts?.removeEventListener('loadingdone', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [geometryTokens, rangesKey, sentenceMode, fontSize, languageCode, expressionMap, textId, viewportRef, templateRef, setCurrentPage]);

  return { ...result, geometryTokens };
}